import React, { useState, useEffect } from 'react';
import { getMyCreations, getGenomeLineage } from '../api';
import AgentCard from '../components/AgentCard';
import CardGrid from '../components/CardGrid';
import styled from 'styled-components';

// --- Styled Components ---
const Pre = styled.pre`
  background: #000;
  padding: 1rem;
  border-radius: 5px;
  overflow-x: auto;
`;

const LineageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
`;

const LineageNode = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 1rem;
`;

const NodeCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.$isLatest ? props.theme.colors.accent : props.theme.colors.surface2};
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  width: 300px;
  cursor: pointer;
  box-shadow: ${props => props.theme.shadow};
  transition: all 0.2s;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Arrow = styled.div`
  width: 2px;
  height: 30px;
  background: ${props => props.theme.colors.textSecondary};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${props => props.theme.colors.textSecondary};
    transform: translateX(-50%);
  }
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: 2rem;
  border-radius: 8px;
  width: 80%;
  max-width: 700px;
  height: 70vh;
  overflow-y: auto;
`;

// --- LineageView Component ---
const LineageView = ({ lineageData, onCardClick }) => {
  // Show newest at the bottom, oldest at the top
  const sortedLineage = [...lineageData].sort((a, b) => a.lineage.version - b.lineage.version);
  
  return (
    <LineageContainer>
      {sortedLineage.map((genome, index) => (
        <React.Fragment key={genome.metadata.genomeId}>
          {index > 0 && <Arrow />}
          <LineageNode>
            <NodeCard 
              $isLatest={genome.lineage.latest} 
              onClick={() => onCardClick(genome)}
            >
              <h3 style={{margin: '0 0 0.5rem 0', color: 'white'}}>{genome.manifest.name}</h3> 
              <span style={{color: 'white'}}>v{genome.lineage.version} {genome.lineage.latest && '(Latest)'}</span>
              <br />
              <span style={{ fontSize: '0.8rem', color: '#888' }}>
                ID: {genome.metadata.genomeId.slice(0, 8)}...
              </span>
            </NodeCard>
          </LineageNode>
        </React.Fragment>
      ))}
    </LineageContainer>
  );
};

// --- LineagePage Component ---
const LineagePage = () => {
  const [creations, setCreations] = useState([]);
  const [selectedGenomeId, setSelectedGenomeId] = useState(null);
  const [lineage, setLineage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalContent, setModalContent] = useState(null);

  useEffect(() => {
    getMyCreations()
      .then(res => setCreations(res.data))
      .catch(err => setError("Failed to fetch creations"))
      .finally(() => setLoading(false));
  }, []);
  
  const handleSelect = async (genomeId) => {
    setSelectedGenomeId(genomeId);
    setLoading(true);
    try {
      const res = await getGenomeLineage(genomeId);
      setLineage(res.data);
    } catch (err) {
      setError("Failed to fetch lineage");
    } finally {
      setLoading(false);
    }
  };

  if (loading && !creations.length) return <div>Loading...</div>;
  if (error) return <div style={{color: 'red'}}>{error}</div>;

  return (
    <div>
      {!selectedGenomeId ? (
        <>
          <h2>View Genome Lineage</h2>
          <p>Select a genome to see its family tree.</p>
          <CardGrid>
            {creations.map(genome => (
              <AgentCard 
                key={genome.metadata.genomeId} 
                genome={genome}
                onClick={() => handleSelect(genome.metadata.genomeId)}
              />
            ))}
          </CardGrid>
        </>
      ) : (
        <>
          <button onClick={() => setSelectedGenomeId(null)} style={{all: 'unset', cursor: 'pointer', color: 'white'}}>&larr; Back to List</button>
          <h2>Lineage for {creations.find(g => g.metadata.genomeId === selectedGenomeId)?.manifest.name}</h2>
          {loading ? (
            <p>Loading lineage...</p>
          ) : (
            <LineageView lineageData={lineage} onCardClick={setModalContent} />
          )}
        </>
      )}
      
      {modalContent && (
        <ModalBackdrop onClick={() => setModalContent(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>Details for v{modalContent.lineage.version}</h3>
            <Pre>{JSON.stringify(modalContent, null, 2)}</Pre>
            <button onClick={() => setModalContent(null)}>Close</button>
          </ModalContent>
        </ModalBackdrop>
      )}
    </div>
  );
};

export default LineagePage;